import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, Modal, PasswordInput, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { useChangeUserPassword } from './usersApi';
import type { UserDto } from './types';

interface Props {
  opened: boolean;
  onClose: () => void;
  user: UserDto | null;
}

export function ChangePasswordModal({ opened, onClose, user }: Props) {
  const { t } = useTranslation();
  const mutation = useChangeUserPassword();

  const form = useForm({
    initialValues: { newPassword: '', confirm: '' },
    validate: {
      newPassword: (v) => (v.length < 8 ? t('users.changePassword.tooShort') : null),
      confirm: (v, values) =>
        v === values.newPassword ? null : t('users.changePassword.mismatch'),
    },
  });

  useEffect(() => {
    if (opened) {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const submit = async (values: typeof form.values) => {
    if (!user) return;
    try {
      await mutation.mutateAsync({
        id: user.userId,
        request: { newPassword: values.newPassword },
      });
      notifications.show({
        color: 'green',
        title: t('users.changePassword.success'),
        message: user.fullNameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('users.changePassword.title')}
      centered
      size="md"
    >
      {user && (
        <Stack gap="md">
          <Text c="var(--wheels-ink-muted)" fz="sm">
            {t('users.changePassword.body', { name: user.fullNameAr })}
          </Text>
          <form onSubmit={form.onSubmit(submit)}>
            <Stack gap="md">
              <PasswordInput
                label={t('users.changePassword.newLabel')}
                withAsterisk
                {...form.getInputProps('newPassword')}
              />
              <PasswordInput
                label={t('users.changePassword.confirmLabel')}
                withAsterisk
                {...form.getInputProps('confirm')}
              />
              <Group justify="flex-end" gap="sm">
                <Button variant="default" onClick={onClose} disabled={mutation.isPending}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" loading={mutation.isPending}>
                  {t('common.save')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      )}
    </Modal>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
